import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Circle, Mail, Phone, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { API_BASE_URL } from "@/lib/consts";
import { HelmCompass } from "@/components/helm-logo";

interface VerificationState {
	email_verified: boolean;
	phone_verified: boolean;
}

export const Route = createFileRoute("/auth/verify")({
	component: VerifyPage,
});

function VerifyPage() {
	const [verificationState, setVerificationState] =
		useState<VerificationState | null>(null);
	const [resending, setResending] = useState(false);
	const [resendMessage, setResendMessage] = useState<string | null>(null);

	useEffect(() => {
		const stateJson = localStorage.getItem("verification_state");
		if (stateJson) {
			try {
				setVerificationState(JSON.parse(stateJson));
			} catch {
				setVerificationState(null);
			}
		}
	}, []);

	const handleResendEmail = async () => {
		setResending(true);
		setResendMessage(null);
		try {
			const tempToken = localStorage.getItem("verification_token");
			if (!tempToken) {
				setResendMessage(
					"No verification session found. Please log in again.",
				);
				return;
			}

			const res = await fetch(
				`${API_BASE_URL}/auth/resend-verification-email`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ temp_token: tempToken }),
				},
			);

			if (res.ok) {
				setResendMessage("Verification email sent! Check your inbox.");
			} else {
				const body = await res.json().catch(() => null);
				setResendMessage(
					body?.detail || "Failed to resend verification email.",
				);
			}
		} catch {
			setResendMessage("An unexpected error occurred.");
		} finally {
			setResending(false);
		}
	};

	const handleReturnToLogin = () => {
		localStorage.removeItem("verification_token");
		localStorage.removeItem("verification_state");
	};

	if (!verificationState) {
		return (
			<div className="flex min-h-svh items-center justify-center bg-muted/40 p-6">
				<div className="mx-auto w-full max-w-md space-y-8">
					<div className="flex justify-center">
						<HelmCompass
							size={32}
							color="hsl(22 82% 31%)"
							strokeWidth={1.2}
						/>
					</div>
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<h1 className="text-2xl font-semibold">
							No Verification Required
						</h1>
						<p className="text-muted-foreground">
							No pending verification was found. Please log in to continue.
						</p>
						<Button asChild className="mt-2 w-full">
							<Link to="/login">Go to Login</Link>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-svh items-center justify-center bg-muted/40 p-6">
			<div className="mx-auto w-full max-w-md space-y-8">
				<div className="flex justify-center">
					<HelmCompass
						size={32}
						color="hsl(22 82% 31%)"
						strokeWidth={1.2}
					/>
				</div>

				<div className="flex flex-col gap-6 rounded-lg border bg-card p-8 shadow-sm">
					<div className="text-center">
						<h1 className="text-2xl font-semibold">
							Complete Verification
						</h1>
						<p className="mt-2 text-muted-foreground">
							Please complete the following steps to access your account.
						</p>
					</div>

					<div className="space-y-4">
						{/* Email verification status */}
						<div className="flex items-start gap-3 rounded-md border p-4">
							<div className="mt-0.5">
								{verificationState.email_verified ? (
									<CheckCircle2 className="h-5 w-5 text-green-600" />
								) : (
									<Circle className="h-5 w-5 text-muted-foreground" />
								)}
							</div>
							<div className="flex-1">
								<div className="flex items-center gap-2">
									<Mail className="h-4 w-4 text-muted-foreground" />
									<span className="font-medium">Email Verification</span>
								</div>
								{verificationState.email_verified ? (
									<p className="mt-1 text-sm text-green-600">
										Verified
									</p>
								) : (
									<div className="mt-2 space-y-2">
										<p className="text-sm text-muted-foreground">
											Check your inbox for a verification link.
										</p>
										<Button
											variant="outline"
											size="sm"
											onClick={handleResendEmail}
											disabled={resending}
										>
											{resending ? (
												<>
													<Spinner className="mr-2 size-3" />
													Sending...
												</>
											) : (
												"Resend Verification Email"
											)}
										</Button>
										{resendMessage && (
											<p className="text-xs text-muted-foreground">
												{resendMessage}
											</p>
										)}
									</div>
								)}
							</div>
						</div>

						{/* Phone verification status */}
						<div className="flex items-start gap-3 rounded-md border p-4">
							<div className="mt-0.5">
								{verificationState.phone_verified ? (
									<CheckCircle2 className="h-5 w-5 text-green-600" />
								) : (
									<Circle className="h-5 w-5 text-muted-foreground" />
								)}
							</div>
							<div className="flex-1">
								<div className="flex items-center gap-2">
									<Phone className="h-4 w-4 text-muted-foreground" />
									<span className="font-medium">Phone Verification</span>
								</div>
								{verificationState.phone_verified ? (
									<p className="mt-1 text-sm text-green-600">
										Verified
									</p>
								) : (
									<p className="mt-1 text-sm text-muted-foreground">
										Phone verification will be completed during
										account setup.
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<Button asChild variant="ghost" size="sm" onClick={handleReturnToLogin}>
							<Link to="/login">
								<ArrowLeft className="mr-2 h-4 w-4" />
								Return to Login
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
